/**
 * AiJobsHistory — V1.9.2
 *
 * Admin/Superadmin page (`/superadmin/ai-jobs`) to inspect the async
 * job queue. Shows:
 *  - Status breakdown (done / running / pending / error)
 *  - Per-kind avg + max duration + error rate
 *  - Recent errors table
 *  - Recent jobs list (filterable by status + kind) with Retry action
 *
 * Auto-refreshes every 15 s so an admin can watch jobs move through the
 * pipeline in real time.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    ArrowClockwise,
    Warning,
    CheckCircle,
    Clock,
    Cpu,
    ShieldCheck,
} from "@phosphor-icons/react";

const REFRESH_MS = 15000;

function formatDuration(ms) {
    if (ms == null) return "—";
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
    return `${(ms / 60_000).toFixed(1)} m`;
}

function statusPill(status) {
    const styles = {
        done: "bg-emerald-100 text-emerald-700 border-emerald-200",
        running: "bg-blue-100 text-blue-700 border-blue-200",
        pending: "bg-amber-100 text-amber-700 border-amber-200",
        error: "bg-red-100 text-red-700 border-red-200",
    };
    const cls = styles[status] || "bg-gray-100 text-gray-600 border-gray-200";
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider border rounded-full ${cls}`}>
            {status}
        </span>
    );
}

export default function AiJobsHistory() {
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [kindFilter, setKindFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [refreshedAt, setRefreshedAt] = useState(null);

    const fetchAll = useCallback(async () => {
        try {
            const [statsRes, listRes] = await Promise.all([
                api.get("/superadmin/ai-jobs/stats"),
                api.get(`/superadmin/ai-jobs?limit=100${statusFilter ? `&status=${statusFilter}` : ""}${kindFilter ? `&kind=${kindFilter}` : ""}`),
            ]);
            setStats(statsRes.data);
            setItems(listRes.data?.items || []);
            setRefreshedAt(new Date());
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load job stats");
        } finally {
            setLoading(false);
        }
    }, [statusFilter, kindFilter]);

    useEffect(() => {
        fetchAll();
        const t = setInterval(fetchAll, REFRESH_MS);
        return () => clearInterval(t);
    }, [fetchAll]);

    const retry = async (jobId) => {
        try {
            await api.post(`/superadmin/ai-jobs/${jobId}/retry`);
            toast.success("Job requeued — will pick up within 10 s.");
            fetchAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Retry failed");
        }
    };

    const byStatus = Object.fromEntries((stats?.by_status || []).map((r) => [r._id, r.count]));
    const byKind = stats?.by_kind || [];
    const recentErrors = stats?.recent_errors || [];

    const mountedAtRef = React.useRef(Date.now());

    // V1.9.4 — surface actionable alerts so an admin can react before users notice.
    // V1.9.5 — cooldown: suppress pending-backlog banner for 60 s after mount
    // to prevent healthy-burst false alarms right after the poller starts.
    const [nowMs, setNowMs] = React.useState(() => Date.now());
    React.useEffect(() => {
        // Bump `nowMs` once at 61 s so the useMemo re-evaluates and lifts
        // the cooldown. No interval needed after that.
        const t = setTimeout(() => setNowMs(Date.now()), 61_000);
        return () => clearTimeout(t);
    }, []);
    const inCooldown = (nowMs - mountedAtRef.current) < 60_000;

    const alerts = React.useMemo(() => {
        const out = [];
        const pending = byStatus.pending || 0;
        if (pending > 20 && !inCooldown) {
            out.push({
                severity: "warn",
                msg: `${pending} jobs backlogged — poller may be behind or a worker crashed. Check worker id + concurrency below.`,
            });
        }
        byKind.forEach((k) => {
            if (k.count >= 10) {
                const rate = k.errors / k.count;
                if (rate > 0.10) {
                    out.push({
                        severity: "error",
                        msg: `Error rate on '${k._id}' is ${(rate * 100).toFixed(0)}% (${k.errors}/${k.count}). Investigate recent failures.`,
                    });
                }
            }
        });
        return out;
    }, [byStatus, byKind, inCooldown]);

    return (
        <div className="min-h-screen bg-paper py-8 px-4" data-testid="ai-jobs-history-page">
            <div className="max-w-6xl mx-auto">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-ink/60 hover:text-cobalt mb-6"
                >
                    ← Back
                </Link>

                <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-2 flex items-center gap-1">
                            <ShieldCheck size={12} weight="fill" /> Admin only
                        </p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-ink">
                            AI Jobs — Live queue &amp; history
                        </h1>
                        <p className="text-sm text-ink/60 mt-2">
                            Every long-running LLM call routes through this queue. Watch it
                            to spot slow days early.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-ink/50">
                        <ArrowClockwise size={12} className="animate-spin" style={{ animationDuration: "3s" }} />
                        Auto-refreshing every 15 s
                        {refreshedAt && (
                            <span className="tabular-nums">· last {refreshedAt.toLocaleTimeString()}</span>
                        )}
                    </div>
                </div>

                {/* V1.9.4 — Alert banner */}
                {alerts.length > 0 && (
                    <div className="mb-6 space-y-2" data-testid="ai-jobs-alerts">
                        {alerts.map((a, i) => (
                            <div
                                key={i}
                                data-testid={`ai-jobs-alert-${a.severity}`}
                                className={`flex items-start gap-3 rounded-2xl border p-4 ${
                                    a.severity === "error"
                                        ? "bg-red-50 border-red-300 text-red-800"
                                        : "bg-amber-50 border-amber-300 text-amber-800"
                                }`}
                            >
                                <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
                                <div className="flex-1 text-sm font-semibold leading-snug">
                                    {a.msg}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Status counters */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                    <CounterCard label="Done" value={byStatus.done || 0} icon={CheckCircle} tone="emerald" testId="ai-jobs-count-done" />
                    <CounterCard label="Running" value={byStatus.running || 0} icon={Cpu} tone="blue" testId="ai-jobs-count-running" />
                    <CounterCard label="Pending" value={byStatus.pending || 0} icon={Clock} tone="amber" testId="ai-jobs-count-pending" />
                    <CounterCard label="Errored" value={byStatus.error || 0} icon={Warning} tone="red" testId="ai-jobs-count-error" />
                </div>

                {/* Per-kind breakdown */}
                <div className="bg-white border border-ink/10 rounded-2xl p-5 sm:p-6 mb-6 shadow-sm">
                    <h2 className="font-heading text-lg font-black tracking-tight text-ink mb-4">
                        By kind
                    </h2>
                    {byKind.length === 0 ? (
                        <p className="text-sm text-ink/50">No jobs yet.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="ai-jobs-kind-table">
                                <thead>
                                    <tr className="border-b border-ink/10 text-[10px] font-black uppercase tracking-wider text-ink/50">
                                        <th className="text-left pb-2">Kind</th>
                                        <th className="text-right pb-2">Total</th>
                                        <th className="text-right pb-2">Errors</th>
                                        <th className="text-right pb-2">Error rate</th>
                                        <th className="text-right pb-2">Avg duration</th>
                                        <th className="text-right pb-2">Max duration</th>
                                        <th className="text-right pb-2">In-flight / cap</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byKind.map((k) => {
                                        const errRate = k.count > 0 ? ((k.errors / k.count) * 100).toFixed(1) : "0";
                                        const hot = k.errors > 0 || (k.avg_duration_ms || 0) > 60000;
                                        const conc = (stats?.concurrency || []).find((c) => c.kind === k._id);
                                        const active = conc?.active ?? 0;
                                        const cap = conc?.cap ?? "—";
                                        const atCap = conc && active >= cap;
                                        return (
                                            <tr key={k._id} className="border-b border-ink/5 hover:bg-cream/40">
                                                <td className="py-2.5 font-semibold" data-testid={`ai-jobs-kind-${k._id}`}>
                                                    {k._id}
                                                </td>
                                                <td className="py-2.5 text-right tabular-nums">{k.count}</td>
                                                <td className={`py-2.5 text-right tabular-nums ${k.errors > 0 ? "text-red-600 font-bold" : "text-ink/50"}`}>
                                                    {k.errors}
                                                </td>
                                                <td className={`py-2.5 text-right tabular-nums ${errRate > 0 ? "text-red-600 font-bold" : "text-ink/50"}`}>
                                                    {errRate}%
                                                </td>
                                                <td className={`py-2.5 text-right tabular-nums ${hot ? "text-amber-600 font-semibold" : ""}`}>
                                                    {formatDuration(k.avg_duration_ms)}
                                                </td>
                                                <td className="py-2.5 text-right tabular-nums text-ink/70">
                                                    {formatDuration(k.max_duration_ms)}
                                                </td>
                                                <td
                                                    className={`py-2.5 text-right tabular-nums ${atCap ? "text-red-600 font-bold" : "text-ink/60"}`}
                                                    data-testid={`ai-jobs-concurrency-${k._id}`}
                                                >
                                                    {conc ? `${active} / ${cap}` : "—"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {stats?.worker_id && (
                                <p className="text-[10px] text-ink/40 mt-3 tabular-nums">
                                    Worker: <code>{stats.worker_id}</code>
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Recent errors */}
                {recentErrors.length > 0 && (
                    <div className="bg-red-50/60 border border-red-200 rounded-2xl p-5 mb-6" data-testid="ai-jobs-recent-errors">
                        <h2 className="font-heading text-base font-black tracking-tight text-red-700 mb-3 flex items-center gap-2">
                            <Warning size={16} weight="fill" /> Recent errors
                        </h2>
                        <div className="space-y-2">
                            {recentErrors.map((e) => (
                                <div key={e.id} className="flex items-start gap-2 text-xs">
                                    <span className="px-1.5 py-0.5 bg-white border border-red-200 rounded font-semibold text-red-700 shrink-0">
                                        {e.kind}
                                    </span>
                                    <span className="text-ink/80 flex-1 line-clamp-2">
                                        {e.error || "unknown"}
                                    </span>
                                    <button
                                        onClick={() => retry(e.id)}
                                        className="text-cobalt hover:underline shrink-0 font-semibold"
                                        data-testid={`ai-jobs-retry-${e.id}`}
                                    >
                                        Retry
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="flex flex-wrap gap-2 mb-4">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        data-testid="ai-jobs-filter-status"
                        className="px-3 py-1.5 text-xs bg-white border border-ink/15 rounded-full"
                    >
                        <option value="">All statuses</option>
                        <option value="pending">Pending</option>
                        <option value="running">Running</option>
                        <option value="done">Done</option>
                        <option value="error">Error</option>
                    </select>
                    <select
                        value={kindFilter}
                        onChange={(e) => setKindFilter(e.target.value)}
                        data-testid="ai-jobs-filter-kind"
                        className="px-3 py-1.5 text-xs bg-white border border-ink/15 rounded-full"
                    >
                        <option value="">All kinds</option>
                        {byKind.map((k) => (
                            <option key={k._id} value={k._id}>{k._id}</option>
                        ))}
                    </select>
                </div>

                {/* Recent jobs table */}
                <div className="bg-white border border-ink/10 rounded-2xl shadow-sm overflow-hidden" data-testid="ai-jobs-recent-table">
                    <div className="p-5 sm:p-6 border-b border-ink/10">
                        <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                            Recent jobs ({items.length})
                        </h2>
                    </div>
                    {loading && !items.length ? (
                        <div className="p-8 text-center text-xs text-ink/50">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-8 text-center text-xs text-ink/50">No jobs match the current filters.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-cream/60 border-b border-ink/10">
                                    <tr className="text-[10px] font-black uppercase tracking-wider text-ink/50">
                                        <th className="text-left px-4 py-2">Kind</th>
                                        <th className="text-left px-4 py-2">Status</th>
                                        <th className="text-right px-4 py-2">Duration</th>
                                        <th className="text-left px-4 py-2">Created</th>
                                        <th className="text-right px-4 py-2">Attempts</th>
                                        <th className="text-right px-4 py-2 pr-5">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((j) => (
                                        <tr key={j.id} className="border-b border-ink/5" data-testid={`ai-jobs-row-${j.id}`}>
                                            <td className="px-4 py-2.5 font-semibold text-ink/80">{j.kind}</td>
                                            <td className="px-4 py-2.5">{statusPill(j.status)}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums text-ink/70">{formatDuration(j.duration_ms)}</td>
                                            <td className="px-4 py-2.5 text-xs text-ink/50 tabular-nums">
                                                {j.created_at ? new Date(j.created_at).toLocaleTimeString() : "—"}
                                            </td>
                                            <td className="px-4 py-2.5 text-right tabular-nums text-ink/50">{j.attempts || 0}</td>
                                            <td className="px-4 py-2.5 text-right pr-5">
                                                {(j.status === "done" || j.status === "error") && (
                                                    <button
                                                        onClick={() => retry(j.id)}
                                                        className="text-cobalt hover:underline text-xs font-semibold"
                                                    >
                                                        Retry
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CounterCard({ label, value, icon: Icon, tone, testId }) {
    const tones = {
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        blue: "bg-blue-50 text-blue-700 border-blue-200",
        amber: "bg-amber-50 text-amber-700 border-amber-200",
        red: "bg-red-50 text-red-700 border-red-200",
    };
    return (
        <div className={`rounded-2xl border p-4 ${tones[tone] || ""}`} data-testid={testId}>
            <div className="flex items-center justify-between">
                <p className="text-[10px] font-black uppercase tracking-wider opacity-70">{label}</p>
                <Icon size={16} weight="fill" className="opacity-70" />
            </div>
            <p className="text-3xl font-black tabular-nums mt-1.5">{value.toLocaleString()}</p>
        </div>
    );
}
